import React, { useEffect, useState } from "react"
import { Search, X } from "lucide-react"

import KeywordSuggestionsDetailsModal from "../../../components/Common/Modal/KeywordSuggestionsDetailsModal"

/**
 * "Filter by app" search - the same search-box-then-modal pattern as UGC
 * Explorer's own "Find an App" box (type a name, hit Search, pick the app
 * from the results modal), just sized down to a toolbar-width bar since
 * this section doesn't need a whole "Find an App" card of its own. Reuses
 * the same KeywordSuggestionsDetailsModal every other search entry point in
 * the app opens, so the search experience matches everywhere.
 */
const AppSearchBar = ({ onSelectApp, onClear, selectedAppName = "", placeholder = "Search for an app…" }) => {
    const [searchTerm, setSearchTerm] = useState(selectedAppName)
    const [showModal, setShowModal] = useState(false)

    // The applied app's name lives in the input itself - re-sync whenever the
    // parent's applied app changes (picked, restored from URL, or cleared).
    useEffect(() => {
        setSearchTerm(selectedAppName)
    }, [selectedAppName])

    const handleClear = () => {
        setSearchTerm("")
        if (selectedAppName) onClear?.()
    }

    const handleSearch = () => {
        if (!searchTerm.trim()) return
        setShowModal(true)
    }

    const handleSelectApp = (app) => {
        onSelectApp?.(app)
        setShowModal(false)
    }

    return (
        <>
            <div className="influencer-app-search-row">
                <div className="influencer-app-search-input">
                    <Search size={14} color="#9ca3af" style={{ flexShrink: 0, marginLeft: "12px" }} />
                    <input
                        type="text"
                        value={searchTerm}
                        placeholder={placeholder}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                    />
                    {searchTerm && (
                        <button
                            type="button"
                            onClick={handleClear}
                            aria-label="Clear search"
                            title="Clear search"
                            className="influencer-app-search-clear"
                        >
                            <X size={14} />
                        </button>
                    )}
                </div>
                <button
                    type="button"
                    onClick={handleSearch}
                    className="influencer-btn-primary influencer-app-search-btn"
                    style={{ height: "44px", padding: "0 18px" }}
                >
                    <Search size={14} /> Search
                </button>
            </div>

            <KeywordSuggestionsDetailsModal
                show={showModal}
                handleClose={() => setShowModal(false)}
                title="Search Apps"
                searchTerm={searchTerm}
                onSearchChange={e => setSearchTerm(e.target.value)}
                onSearchClick={() => {}}
                onSelectApp={handleSelectApp}
                selectButtonLabel="Filter Influencers"
            />

            <style>{`
                .influencer-app-search-row {
                    display: flex;
                    align-items: center;
                    gap: 10px;
                    flex-wrap: wrap;
                }
                .influencer-app-search-input {
                    display: flex;
                    align-items: center;
                    height: 44px;
                    flex: 1 1 220px;
                    min-width: 0;
                    max-width: 340px;
                    background: #1c1d27;
                    border: 1px solid rgba(255,255,255,0.1);
                    border-radius: 10px;
                    transition: border-color 0.15s ease;
                }
                .influencer-app-search-input:focus-within { border-color: rgba(246,37,90,0.5); }
                .influencer-app-search-input input {
                    flex: 1;
                    min-width: 0;
                    background: transparent;
                    border: none;
                    outline: none;
                    color: #fff;
                    font-size: 13px;
                    font-family: Rubik, sans-serif;
                    padding: 0 10px;
                }
                .influencer-app-search-input input::placeholder { color: #6b7280; }
                .influencer-app-search-clear {
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    flex-shrink: 0;
                    width: 26px;
                    height: 26px;
                    margin-right: 8px;
                    background: transparent;
                    border: none;
                    border-radius: 6px;
                    color: #9ca3af;
                    cursor: pointer;
                }
                .influencer-app-search-clear:hover { color: #fff; background: rgba(255,255,255,0.08); }
                @media (max-width: 480px) {
                    .influencer-app-search-input { max-width: none; flex-basis: 100%; }
                    .influencer-app-search-btn { flex: 1 1 100%; }
                }
            `}</style>
        </>
    )
}

export default AppSearchBar
